import { c as _c } from "react/compiler-runtime";
import type { CSSProperties } from 'react';
import Image from '@/components/media/StorefrontImage';
import { IndustryQuoteForm } from '@/components/hubspot';
import { toStorefrontMediaUrl } from '@/lib/media-url';
import styles from './IndustryHero.module.css';
export interface IndustryHeroImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}
export interface IndustryHeroContent {
  blockId: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  reviewHref: string;
  ratingText: string;
  dotBackground: string;
  formAnchorHref: string;
  /**
   * Optional primary CTA rendered inside the hero copy column, mirroring
   * production Magento pages whose hero markup includes an inline
   * `<a class="main-btn">LEARN MORE</a>` after the subtitle. Used today by
   * construction, religious-institution, and car-wash. When omitted, the
   * hero renders no CTA in the copy column.
   */
  ctaButton?: {
    text: string;
    href: string;
  };
  /**
   * When set, the hero's right column renders this image collage instead of
   * the HubSpot quote form. Mirrors Magento's `<div class="header_image">`
   * pattern (primary image + smaller overlay). Used by industries whose
   * production hero is image-on-the-right rather than form-on-the-right
   * (e.g. construction). The first image is the primary; the second, if
   * present, is overlaid at the bottom-left.
   */
  rightColumnImages?: IndustryHeroImage[];
}
export interface IndustryHeroProps {
  content: IndustryHeroContent;
}
export function IndustryHero(t0) {
  const $ = _c(41);
  const {
    content
  } = t0;
  const t1 = content.blockId;
  let t2;
  if ($[0] !== content.dotBackground) {
    t2 = toStorefrontMediaUrl(content.dotBackground);
    $[0] = content.dotBackground;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `url(${t2})`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      "--hero-dot-bg": t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = t4 as CSSProperties;
  let t6;
  if ($[4] !== content.title || $[5] !== content.titleAccent) {
    t6 = content.title.replace(content.titleAccent, "").trim();
    $[4] = content.title;
    $[5] = content.titleAccent;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== content.titleAccent) {
    t7 = <span data-cms-field="titleAccent">{content.titleAccent}</span>;
    $[7] = content.titleAccent;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <h1 className={styles.heroTitle} data-cms-field="title">{t6}{" "}{t7}</h1>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== content.subtitle) {
    t9 = <h3 className={styles.heroSubtitle} data-cms-field="subtitle">{content.subtitle}</h3>;
    $[12] = content.subtitle;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== content.ctaButton) {
    t10 = content.ctaButton ? <a href={content.ctaButton.href} className={`${styles.heroCta} main-btn text-white`} data-cms-field="ctaButton"><strong>{content.ctaButton.text}</strong></a> : null;
    $[14] = content.ctaButton;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className={styles.star} data-star-icon={true} />;
    t12 = <span className={styles.star} data-star-icon={true} />;
    t13 = <span className={styles.star} data-star-icon={true} />;
    t14 = <span className={styles.star} data-star-icon={true} />;
    t15 = <span className={styles.star} data-star-icon={true} />;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
  }
  let t16;
  if ($[21] !== content.reviewHref) {
    t16 = <a href={content.reviewHref} target="_blank" className={styles.ratingStars} data-cms-field="review.href" data-cms-href-field="review.href">{t11}{t12}{t13}{t14}{t15}</a>;
    $[21] = content.reviewHref;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== content.ratingText) {
    t17 = <p className={styles.ratingText} data-cms-field="ratingText">{content.ratingText}</p>;
    $[23] = content.ratingText;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t10 || $[26] !== t16 || $[27] !== t17 || $[28] !== t8 || $[29] !== t9) {
    t18 = <div className={styles.heroCopy}>{t8}{t9}{t10}{t16}{t17}</div>;
    $[25] = t10;
    $[26] = t16;
    $[27] = t17;
    $[28] = t8;
    $[29] = t9;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== content.formAnchorHref || $[32] !== content.rightColumnImages) {
    t19 = content.rightColumnImages && content.rightColumnImages.length > 0 ? <div className={styles.heroImageStack}><a id="formGetQuote" href={content.formAnchorHref} className={styles.formAnchor} />{content.rightColumnImages.map(_temp)}</div> : <div className={styles.heroFormCard}><a id="formGetQuote" href={content.formAnchorHref} className={styles.formAnchor} /><IndustryQuoteForm className={styles.embeddedForm} /></div>;
    $[31] = content.formAnchorHref;
    $[32] = content.rightColumnImages;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <div className={styles.container}><div className={styles.heroGrid}>{t18}{t19}</div></div>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== content.blockId || $[38] !== t20 || $[39] !== t5) {
    t21 = <section className={styles.hero} data-cms-block-id={t1} style={t5}>{t20}</section>;
    $[37] = content.blockId;
    $[38] = t20;
    $[39] = t5;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return t21;
}
function _temp(img, idx) {
  return <Image key={img.src} src={img.src} data-src={img.src} alt={img.alt} width={img.width} height={img.height} className={idx === 0 ? styles.heroImagePrimary : styles.heroImageOverlay} priority={idx === 0} sizes="(max-width: 1023px) 88vw, 600px" />;
}